import { useState, useEffect, useCallback, createContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth } from "../helpers/authentication";
import {
  DIALOG_NAME,
  useOpenParamDialog,
} from "../helpers/useDialogSearchParam";
import userModel from "../lib/firebase/userModel";
import { userKeys } from "../lib/queryKeys";
import mixpanel from "mixpanel-browser";
import { trackEvent } from "../helpers/mixpanel";
import banModel from "../lib/firebase/banModel";
import suspensionModel from "../lib/firebase/suspensionModel";
import hasAlertModel from "../lib/firebase/hasAlertModel";
import { useBreakpoints } from "../lib/breakpoints";
import {
  captureForensicAuditIdFromUrl,
  PENDING_FORENSIC_AUDIT_PDF_KEY,
  unlockDeliverForensicAuditPdfOnce,
} from "../lib/api/forensicAuditApi";

export const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const authListenerRef = useRef(null);
  const initializedRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  // Simplified userInfo query - doesn't block authentication
  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    error: userInfoError,
  } = useQuery({
    queryKey: userKeys.user(user?.uid),
    queryFn: async () => {
      if (process.env.NODE_ENV === "development") {
        console.log("[AuthProvider] Fetching user info for:", user.uid);
      }
      try {
        const result = await userModel.getOneById(user.uid);
        if (process.env.NODE_ENV === "development") {
          console.log("[AuthProvider] User info result:", result);
        }
        return result;
      } catch (error) {
        console.error("[AuthProvider] Error fetching user info:", error);
        // Return a default user object if Firestore query fails
        return {
          id: user.uid,
          email: user.email,
          displayName: user.displayName || user.email,
        };
      }
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 0, // No retries
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const openLoginParamDialog = useOpenParamDialog(DIALOG_NAME.login);
  const requireLogin = useCallback(() => {
    if (isMobile) {
      navigate("/login");
      return;
    }
    openLoginParamDialog();
  }, [isMobile, navigate, openLoginParamDialog]);
  const requireSignup = useOpenParamDialog(DIALOG_NAME.signup);

  const isGuest = !user && !loading;
  const isThirdPartyAuthUser =
    user && user?.providerData[0]?.providerId !== "password";

  // Authentication is complete when Firebase auth state is determined
  const isAuthenticated = !!user && !loading;

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("[AuthProvider] State update:", {
        user: !!user,
        loading,
        userInfo: !!userInfo,
        isUserInfoLoading,
        userInfoError: !!userInfoError,
        timestamp: new Date().toISOString(),
      });
    }
  }, [user, loading, userInfo, isUserInfoLoading, userInfoError]);

  useEffect(() => {
    captureForensicAuditIdFromUrl();
  }, []);

  useEffect(() => {
    if (!user || !userInfo || isUserInfoLoading) return;
    const status = userInfo.subscriptionStatus;
    const paid = status === "active" || status === "trialing";
    if (!paid) return;

    let auditId;
    try {
      auditId = sessionStorage.getItem(PENDING_FORENSIC_AUDIT_PDF_KEY);
    } catch {
      return;
    }
    if (!auditId) return;

    unlockDeliverForensicAuditPdfOnce(auditId)
      .then(() => {
        try {
          sessionStorage.removeItem(PENDING_FORENSIC_AUDIT_PDF_KEY);
        } catch {
          /* ignore */
        }
        if (process.env.NODE_ENV === "development") {
          console.log("[ForensicAudit] PDF delivery triggered for", auditId);
        }
      })
      .catch((err) => {
        console.warn(
          "[ForensicAudit] PDF delivery failed (will retry on next load):",
          err?.message || err
        );
      });
  }, [user, userInfo, isUserInfoLoading]);

  // Main authentication effect - stable and simple: rely on Firebase callback
  useEffect(() => {
    // Prevent multiple auth listeners
    if (initializedRef.current) {
      return;
    }

    const startTime = performance.now();
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[AuthProvider] Starting auth state listener at:",
        new Date().toISOString()
      );
    }

    // Mark as initialized immediately
    initializedRef.current = true;

    // Try to get current user immediately (most common case)
    const currentUser = firebaseAuth.currentUser;
    if (currentUser) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[AuthProvider] Found current user immediately:",
          currentUser.uid
        );
      }
      setUser(currentUser);
      setLoading(false);
      try {
        trackEvent("auth_user_restored", { uid: currentUser.uid });
      } catch (error) {
        console.warn(
          "[AuthProvider] Failed to track auth_user_restored:",
          error
        );
      }

      // Start non-blocking operations immediately
      setTimeout(() => {
        try {
          mixpanel.identify(currentUser.uid);
          banModel.listen();
          suspensionModel.listen();
          hasAlertModel.listen();
        } catch (error) {
          console.error(
            "[AuthProvider] Error in non-blocking operations:",
            error
          );
        }
      }, 0);

      return;
    }

    // Single, stable listener for all environments
    const unsubscribe = onAuthStateChanged(firebaseAuth, (callbackUser) => {
      const authTime = performance.now() - startTime;
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[AuthProvider] Auth state changed in ${authTime.toFixed(
            2
          )}ms at ${new Date().toISOString()}:`,
          callbackUser ? "User logged in" : "User logged out"
        );
      }

      if (callbackUser) {
        if (process.env.NODE_ENV === "development") {
          console.log("[AuthProvider] Setting user:", {
            uid: callbackUser.uid,
            email: callbackUser.email,
            providerId: callbackUser.providerData[0]?.providerId,
            metadata: {
              creationTime: callbackUser.metadata.creationTime,
              lastSignInTime: callbackUser.metadata.lastSignInTime,
            },
            timestamp: new Date().toISOString(),
          });
        }

        // Check if this is a restored session or new login
        const isRestored =
          callbackUser.metadata.lastSignInTime ===
          callbackUser.metadata.creationTime;
        const timeSinceLastSignIn =
          new Date() - new Date(callbackUser.metadata.lastSignInTime);
        const hoursSinceLastSignIn = timeSinceLastSignIn / (1000 * 60 * 60);

        if (process.env.NODE_ENV === "development") {
          console.log("[AuthProvider] Session info:", {
            isRestored,
            hoursSinceLastSignIn: hoursSinceLastSignIn.toFixed(2),
            wasRestored: hoursSinceLastSignIn > 0.1, // More than 6 minutes ago
          });
        }

        setUser(callbackUser);
        try {
          trackEvent("auth_login", {
            uid: callbackUser.uid,
            isRestored,
            hoursSinceLastSignIn: hoursSinceLastSignIn.toFixed(2),
          });
        } catch (error) {
          console.warn("[AuthProvider] Failed to track auth_login:", error);
        }

        setTimeout(() => {
          try {
            mixpanel.identify(callbackUser.uid);
            banModel.listen();
            suspensionModel.listen();
            hasAlertModel.listen();
          } catch (error) {
            console.error(
              "[AuthProvider] Error in non-blocking operations:",
              error
            );
          }
        }, 0);
      } else {
        if (process.env.NODE_ENV === "development") {
          console.log("[AuthProvider] Clearing user:", {
            reason: "auth_state_changed_to_null",
            timestamp: new Date().toISOString(),
            hadUser: !!userRef.current,
            userUid: userRef.current?.uid,
          });
        }

        // Check if storage is still available (helps diagnose storage issues)
        try {
          localStorage.setItem("auth_test", "test");
          localStorage.removeItem("auth_test");
          if (process.env.NODE_ENV === "development") {
            console.log("[AuthProvider] localStorage is available");
          }
        } catch (storageError) {
          console.error(
            "[AuthProvider] localStorage is NOT available - this may be why user was logged out:",
            storageError
          );
          try {
            trackEvent("auth_storage_unavailable", {
              error: storageError.message,
            });
          } catch (trackError) {
            console.warn(
              "[AuthProvider] Failed to track storage error:",
              trackError
            );
          }
        }

        setUser(null);
        queryClient.removeQueries({ queryKey: userKeys.user() });
        
        // SECURITY: Clear localStorage when user signs out to prevent data leakage
        if (user) { // Only clear if we had a user (to avoid clearing on initial load)
          try {
            localStorage.clear();
            sessionStorage.clear();
            console.log("✅ [AUTH] Cleared storage on user logout for security");
          } catch (error) {
            console.error("❌ [AUTH] Failed to clear storage on logout:", error);
          }
        }
        
        try {
          trackEvent("auth_logout", {
            reason: "auth_state_changed",
            hadUser: !!user,
          });
        } catch (error) {
          console.warn("[AuthProvider] Failed to track auth_logout:", error);
        }
      }

      setLoading(false);
    });

    // Store the unsubscribe function in ref
    authListenerRef.current = unsubscribe;

    return () => {
      if (authListenerRef.current) {
        authListenerRef.current();
        authListenerRef.current = null;
      }
    };
    // Intentionally []: register a single onAuthStateChanged for the app lifetime
    // (see initializedRef). queryClient is stable; userRef supplies current user
    // in the null-user path without re-subscribing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, []);

  // Token refresh logic - prevent logout due to expired tokens
  useEffect(() => {
    if (!user) return;

    // Refresh token every 50 minutes (tokens expire after 1 hour)
    // This ensures the token is always fresh and prevents unexpected logouts
    const refreshInterval = setInterval(
      async () => {
        try {
          await user.getIdToken(true); // Force refresh
          if (process.env.NODE_ENV === "development") {
            console.log(
              "[AuthProvider] Token refreshed successfully at:",
              new Date().toISOString()
            );
          }
        } catch (error) {
          console.error("[AuthProvider] Token refresh failed:", error);
          // Don't log out on refresh failure - let Firebase handle it
          // Firebase will trigger onAuthStateChanged if the user needs to be logged out
        }
      },
      50 * 60 * 1000
    ); // 50 minutes

    // Also refresh token when user becomes active after being idle
    let lastActivity = Date.now();
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
    const handleActivity = async () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      lastActivity = now;

      // If user was idle for more than 30 minutes, refresh token when they become active
      if (timeSinceLastActivity > 30 * 60 * 1000) {
        try {
          await user.getIdToken(true);
          if (process.env.NODE_ENV === "development") {
            console.log(
              "[AuthProvider] Token refreshed after user activity (was idle for",
              Math.round(timeSinceLastActivity / 60000),
              "minutes)"
            );
          }
        } catch (error) {
          console.error(
            "[AuthProvider] Token refresh failed after activity:",
            error
          );
        }
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearInterval(refreshInterval);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading, // expose actual auth loading state
        userInfo,
        isUserInfoLoading,
        isGuest,
        isThirdPartyAuthUser,
        requireLogin,
        requireSignup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
